import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  asClock,
  type Answer,
  type Attempt,
  type Exam,
  type ExamTask,
  handIn,
  pauseAttempt,
  writeAnswer,
} from '../../api/exams'
import { asFailure, type Failure } from '../../api/request'
import { useLayoutStep } from '../../frame/layout'
import { useAnnounce } from '../../ui/Announce'
import { Button } from '../../ui/Control'
import { RichText } from '../../ui/RichText'
import { MARKDOWN } from '../../ui/markup'
import { Failed } from '../../ui/Status'
import { useSaving } from '../../ui/useSaving'
import { CardAnswers } from './CardAnswers'
import { Clock } from './Clock'
import { MarkedAnswer, type NotesAt } from './MarkedAnswer'
import { Question } from './Question'
import { Result } from './Result'

/**
 * How much of the split the question gets, and whether it is hidden — kept in this browser rather
 * than on the sitting, because it is a habit of the person reading, not a fact about the paper.
 */
const SPLIT_KEY = 'mylib.sitting.split'
const QUESTION_HIDDEN_KEY = 'mylib.sitting.questionHidden'
const NOTES_AT_KEY = 'mylib.sitting.notesAt'
const SPLIT_MIN = 20
const SPLIT_MAX = 80
const SPLIT_DEFAULT = 50
const SPLIT_STEP = 5

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function rememberedSplit(): number {
  try {
    const kept = Number(window.localStorage.getItem(SPLIT_KEY))
    return Number.isFinite(kept) && kept >= SPLIT_MIN && kept <= SPLIT_MAX ? kept : SPLIT_DEFAULT
  } catch {
    // A browser with storage switched off, or a private window that refuses it. The ratio is a
    // convenience; losing it must never be a broken screen.
    return SPLIT_DEFAULT
  }
}

function rememberSplit(pct: number): void {
  try {
    window.localStorage.setItem(SPLIT_KEY, String(pct))
  } catch {
    // See above.
  }
}

function rememberedNotesAt(): NotesAt {
  try {
    return window.localStorage.getItem(NOTES_AT_KEY) === 'margin' ? 'margin' : 'paragraph'
  } catch {
    // See above.
    return 'paragraph'
  }
}

function rememberNotesAt(where: NotesAt): void {
  try {
    window.localStorage.setItem(NOTES_AT_KEY, where)
  } catch {
    // See above.
  }
}

function rememberedQuestionHidden(): boolean {
  try {
    return window.localStorage.getItem(QUESTION_HIDDEN_KEY) === 'true'
  } catch {
    return false
  }
}

function rememberQuestionHidden(hidden: boolean): void {
  try {
    window.localStorage.setItem(QUESTION_HIDDEN_KEY, String(hidden))
  } catch {
    // See above.
  }
}

/**
 * Sitting a test: the question on one side, what you write on the other.
 *
 * **Side by side where there is room, one at a time where there is not.** On a phone the two halves
 * are two tabs — *Question* and *Answer* — because half of a small screen is not enough for either,
 * and reading a task through a letterbox is worse than switching. On an iPad and at a desk they
 * stand beside each other, which is the whole point of the screen.
 *
 * **One task at a time, in the order of the paper.** Not every task on one long page: a sitting is a
 * thing somebody works through, and a page that scrolls past four other questions is a page that
 * loses the one being answered. The row of numbers at the top jumps anywhere all the same.
 *
 * **Nothing is saved by pressing anything.** The answer is written down a moment after the typing
 * stops, like everything else written in MyLib. *Hand in* says "this is what I wrote" — it locks
 * nothing, and it can be taken back, because the person sitting the paper is the person who set it.
 *
 * **Once there is a judgement, it takes the question's place rather than a place of its own.** The
 * left half has two sides — *Question* and *Judgement* — and the sitting opens on the judgement when
 * there is one, because that is what somebody came back for. Reading a correction means reading a
 * point and looking at the passage it is about, so it belongs beside what was written; underneath it
 * would be a third block on a screen that is exactly one viewport tall, and a long judgement would
 * squeeze the question and the answer to nothing.
 *
 * **The clock runs where it can be seen, and a pause closes the test up.** How long somebody has been
 * sitting at this paper is the one number this screen shows about itself; there is no limit, nothing
 * counts down and nothing happens when it gets long. Pausing stops it *and takes the question and the
 * answer off the screen* — a pause that left them there would be a clock somebody stops and then goes
 * on writing under.
 */
export interface SittingProps {
  readonly exam: Exam
  readonly attempt: Attempt
  /** Called after something was written down, so the page above follows what the sitting now is. */
  readonly onChanged: (attempt: Attempt) => void
  readonly onClose: () => void
}

export function Sitting({ exam, attempt, onChanged, onClose }: SittingProps) {
  const announce = useAnnounce()
  const step = useLayoutStep()
  const narrow = step === 'compact'

  const [at, setAt] = useState(0)
  // Which side is being looked at, or `null` while nobody has said. Left unsaid, a judged sitting
  // opens on its judgement — that is what somebody came back for — and an unjudged one on its
  // question, exactly as before. One value for both layouts: `answer` only ever comes up on a
  // phone, where the halves are tabs rather than panes.
  const [chosen, setChosen] = useState<'question' | 'answer' | 'judgement' | null>(null)
  // Which point of the judgement is being looked at, by its place in the list, and whether the
  // answer is being read or written on. Both are about *this* visit and belong nowhere else.
  const [point, setPoint] = useState<number | null>(null)
  const [writingOn, setWritingOn] = useState(false)
  const [unfound, setUnfound] = useState<readonly number[]>([])
  const [notesAt, setNotesAt] = useState<NotesAt>(rememberedNotesAt)
  const [refused, setRefused] = useState<Failure | null>(null)
  const [splitPct, setSplitPct] = useState(rememberedSplit)
  const [questionHidden, setQuestionHidden] = useState(rememberedQuestionHidden)
  const split = useRef<HTMLDivElement | null>(null)

  const task: ExamTask | undefined = exam.tasks[at]
  const answer: Answer | undefined =
    task === undefined ? undefined : attempt.answers.find((one) => one.taskId === task.id)

  // The latest typed answer, kept a step ahead of the debounced save: switching the markup has to
  // carry exactly this text along rather than waiting for `useSaving`'s half second to pass first.
  // Reset when the task changes, the same moment the editor below is remounted by its own key.
  const answerText = useRef(answer?.text ?? '')
  useEffect(() => {
    answerText.current = answer?.text ?? ''
    // Reset on the task changing, deliberately not on every `answer.text` — that would also fire
    // after this component's own saves and could roll the ref back under a keystroke made while one
    // was in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id])

  const saving = useSaving(async (text: string) => {
    if (task === undefined) {
      return
    }
    try {
      const written = await writeAnswer(attempt.id, task.id, text, answer?.markup ?? MARKDOWN)
      setRefused(null)
      onChanged(written)
    } catch (error) {
      setRefused(asFailure(error))
    }
  })

  const pause = (wanted: boolean) => {
    void (async () => {
      try {
        // What is in the editor goes down before the screen closes over it.
        saving.now()
        const changed = await pauseAttempt(attempt.id, wanted)
        setRefused(null)
        onChanged(changed)
        announce(wanted ? 'Paused. The clock stands.' : 'Going on.')
      } catch (error) {
        setRefused(asFailure(error))
      }
    })()
  }

  const hand = (wanted: boolean) => {
    void (async () => {
      try {
        // Whatever is still in the editor goes down first: handing in a sitting that is missing the
        // last sentence typed would be the one moment this could actually lose somebody's work.
        saving.now()
        const changed = await handIn(attempt.id, wanted)
        setRefused(null)
        onChanged(changed)
        announce(wanted ? 'Handed in.' : 'Open again.')
      } catch (error) {
        setRefused(asFailure(error))
      }
    })()
  }

  const setSplit = (pct: number) => {
    const clamped = clamp(pct, SPLIT_MIN, SPLIT_MAX)
    setSplitPct(clamped)
    rememberSplit(clamped)
  }

  // Pointer capture rather than a window listener: once it is set on press, every further move and
  // the eventual release are delivered to this element even though the pointer has left it — which
  // is the whole point of dragging a divider — and the browser lets go of it by itself on release.
  const onDividerPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onDividerPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId) || split.current === null) {
      return
    }
    const box = split.current.getBoundingClientRect()
    setSplit(((event.clientX - box.left) / box.width) * 100)
  }

  const onDividerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      setSplit(splitPct - SPLIT_STEP)
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      setSplit(splitPct + SPLIT_STEP)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setSplit(SPLIT_MIN)
    } else if (event.key === 'End') {
      event.preventDefault()
      setSplit(SPLIT_MAX)
    }
  }

  const showQuestion = (hidden: boolean) => {
    setQuestionHidden(hidden)
    rememberQuestionHidden(hidden)
  }

  if (task === undefined) {
    return <p className="sitting__empty">This test has no tasks any more.</p>
  }

  // Closed up: paused, and not simply finished. A handed-in sitting keeps its clock stopped and stays
  // readable, because reading it back is what somebody does with it afterwards.
  const closedUp = !attempt.running && attempt.handedInAt === null

  const assessment = attempt.assessment
  // Which side is showing. `answer` is a phone's third state and never reaches the left pane, so at
  // a desk it reads as the question — the answer stands beside it there anyway.
  const side = chosen ?? (assessment !== null ? 'judgement' : 'question')
  // Reading, unless somebody said they want to write on. A judged sitting is come back to in order
  // to be read, and the passages a correction points at can only be marked in the drawn text.
  const reading = assessment !== null && !writingOn
  const left: 'question' | 'judgement' =
    side === 'judgement' && assessment !== null ? 'judgement' : 'question'
  const leftNamed = left === 'question' ? 'The question' : 'The judgement'
  const leftInWords = left === 'question' ? 'the question' : 'the judgement'

  /*
   * The left half: the question while the paper is being written, the judgement once there is one.
   *
   * **Two sides of one place, and not two blocks under each other.** Reading a correction is the
   * same movement as writing the answer was — read a point, look at the passage it is about — so the
   * judgement wants the place the question had, beside what was written, rather than a third block
   * below a screen that is already exactly one viewport tall. It used to be that third block, and a
   * long judgement squeezed the question and the answer to nothing at all.
   */
  const question = (
    <section className="sitting__half sitting__half--question" aria-label={leftNamed}>
      <div className="sitting__questionHead">
        <h2 className="sitting__heading">
          {left === 'question' ? `Task ${String(at + 1)}` : 'The judgement'}
        </h2>
        {/*
          The two sides, named in words. Only where there is a judgement to switch to, and only at a
          desk: on a phone this half is a tab already, and a switch inside a tab would be the same
          choice offered twice.
        */}
        {assessment !== null && !narrow && (
          <div className="sitting__sides" role="group" aria-label="The question or the judgement">
            <Button
              tone={left === 'question' ? 'primary' : 'quiet'}
              aria-pressed={left === 'question'}
              onClick={() => {
                setChosen('question')
              }}
            >
              Question
            </Button>
            <Button
              tone={left === 'judgement' ? 'primary' : 'quiet'}
              aria-pressed={left === 'judgement'}
              onClick={() => {
                setChosen('judgement')
              }}
            >
              Judgement
            </Button>
          </div>
        )}
        {/*
          Only where there is a divider to give the room back to: on a phone the question is a tab
          of its own, and hiding it would leave nothing behind to switch back from.
        */}
        {!narrow && (
          <Button
            icon="collapse"
            onClick={() => {
              showQuestion(true)
            }}
          >
            Hide {leftInWords}
          </Button>
        )}
      </div>
      {left === 'question' ? (
        <>
          <p className="sitting__standard">Measured by: {task.schemeName}</p>
          <Question prompt={task.prompt} label={`The question of task ${String(at + 1)}`} />
        </>
      ) : (
        assessment !== null && <Result assessment={assessment} selected={point} unfound={unfound} />
      )}
    </section>
  )

  const writing = (
    <section className="sitting__half sitting__half--answer" aria-label="Your answer">
      <div className="sitting__questionHead">
        <h2 className="sitting__heading">Your answer</h2>
        {/*
          Only where there is a judgement. An unjudged sitting has one thing to do with its answer
          and does not ask about it.
        */}
        {assessment !== null && task.prompt.kind !== 'cardset' && (
          <>
            {/*
              Where the notes stand. Only while reading, and only where there is a margin to put one
              in: a phone has none, so it has no choice to make either.
            */}
            {reading && !narrow && (
              <div className="sitting__sides" role="group" aria-label="Where the notes stand">
                <Button
                  tone={notesAt === 'paragraph' ? 'primary' : 'quiet'}
                  aria-pressed={notesAt === 'paragraph'}
                  onClick={() => {
                    setNotesAt('paragraph')
                    rememberNotesAt('paragraph')
                  }}
                >
                  At the paragraph
                </Button>
                <Button
                  tone={notesAt === 'margin' ? 'primary' : 'quiet'}
                  aria-pressed={notesAt === 'margin'}
                  onClick={() => {
                    setNotesAt('margin')
                    rememberNotesAt('margin')
                  }}
                >
                  In the margin
                </Button>
              </div>
            )}
            <div
              className="sitting__sides"
              role="group"
              aria-label="Read the answer or write on it"
            >
              <Button
                tone={reading ? 'primary' : 'quiet'}
                aria-pressed={reading}
                onClick={() => {
                  setWritingOn(false)
                }}
              >
                Read
              </Button>
              <Button
                tone={reading ? 'quiet' : 'primary'}
                aria-pressed={!reading}
                onClick={() => {
                  setWritingOn(true)
                }}
              >
                Edit
              </Button>
            </div>
          </>
        )}
      </div>
      {task.prompt.kind !== 'cardset' && reading ? (
        <MarkedAnswer
          // Keyed by the task for the editor's reason: one answer's marks must not be carried into
          // the next task's text.
          key={task.id}
          text={answer?.text ?? ''}
          format={answer?.markup ?? MARKDOWN}
          label={`Your answer to task ${String(at + 1)}`}
          remarks={assessment?.remarks ?? []}
          taskId={task.id}
          notesAt={narrow ? 'paragraph' : notesAt}
          selected={point}
          onSelect={setPoint}
          onUnfound={setUnfound}
        />
      ) : task.prompt.kind === 'cardset' ? (
        <CardAnswers
          // Keyed by the task, so a field mid-debounce does not carry its pending text into the
          // next task's card of the same position.
          key={task.id}
          attemptId={attempt.id}
          taskId={task.id}
          cards={task.prompt.cards ?? []}
          answers={
            new Map(
              attempt.cardAnswers
                .filter((one) => one.taskId === task.id)
                .map((one) => [one.cardId, { text: one.text, markup: one.markup }]),
            )
          }
          onChanged={onChanged}
        />
      ) : (
        <RichText
          // Keyed by the task, so moving between tasks does not carry one answer into the next.
          key={task.id}
          text={answer?.text ?? ''}
          format={answer?.markup ?? MARKDOWN}
          onFormat={async (wanted) => {
            const written = await writeAnswer(attempt.id, task.id, answerText.current, wanted)
            onChanged(written)
          }}
          label={`Your answer to task ${String(at + 1)}`}
          placeholder="Write your answer…"
          purpose="exam-answer"
          initially="text"
          onChange={(text) => {
            answerText.current = text
            saving.typed(text)
          }}
        ></RichText>
      )}
    </section>
  )

  return (
    <div className="sitting">
      <header className="sitting__head">
        <h1 className="sitting__title">{exam.title}</h1>
        <Clock
          // A new answer is a new clock: keyed by what the service last said, so the counting starts
          // from that rather than being written into an old one.
          key={`${String(attempt.elapsedMs)}-${String(attempt.running)}`}
          elapsedMs={attempt.elapsedMs}
          running={attempt.running}
        />
        <div className="sitting__controls">
          {/*
            While the paper is closed up there is one thing to do and it stands on the panel below.
            A second `Go on` here, and a `Hand in` for a paper nobody can see, would be controls for
            a situation somebody has just said they are not in.
          */}
          {!closedUp && attempt.handedInAt === null && (
            <Button
              onClick={() => {
                pause(true)
              }}
            >
              Pause
            </Button>
          )}
          {closedUp ? null : attempt.handedInAt === null ? (
            <Button
              tone="primary"
              onClick={() => {
                hand(true)
              }}
            >
              Hand in
            </Button>
          ) : (
            <Button
              onClick={() => {
                hand(false)
              }}
            >
              Write on
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </div>
      </header>

      {refused !== null && <Failed failure={refused} />}

      {closedUp ? (
        <section className="sitting__closed" aria-label="This test is paused">
          <h2 className="sitting__heading">Paused</h2>
          <p>
            The clock stands at {asClock(attempt.elapsedMs)}, and the paper is closed. Go on when
            you are back.
          </p>
          <div className="controls">
            <Button
              tone="primary"
              onClick={() => {
                pause(false)
              }}
            >
              Go on
            </Button>
          </div>
        </section>
      ) : (
        <>
          <nav className="sitting__tasks" aria-label="The tasks of this test">
            {exam.tasks.map((one, index) => (
              <Button
                key={one.id}
                tone={index === at ? 'primary' : 'quiet'}
                aria-current={index === at ? 'true' : undefined}
                onClick={() => {
                  // What is in the editor goes down before the screen changes under it.
                  saving.now()
                  setAt(index)
                  setChosen('question')
                }}
              >
                {index + 1}
              </Button>
            ))}
          </nav>

          {narrow && (
            <div
              className="sitting__switch"
              role="tablist"
              aria-label="Question, answer or judgement"
            >
              <Button
                tone={side === 'question' ? 'primary' : 'quiet'}
                role="tab"
                aria-selected={side === 'question'}
                onClick={() => {
                  setChosen('question')
                }}
              >
                Question
              </Button>
              <Button
                tone={side === 'answer' ? 'primary' : 'quiet'}
                role="tab"
                aria-selected={side === 'answer'}
                onClick={() => {
                  setChosen('answer')
                }}
              >
                Answer
              </Button>
              {/* A third tab only where there is a third thing. An unjudged sitting is unchanged. */}
              {assessment !== null && (
                <Button
                  tone={side === 'judgement' ? 'primary' : 'quiet'}
                  role="tab"
                  aria-selected={side === 'judgement'}
                  onClick={() => {
                    setChosen('judgement')
                  }}
                >
                  Judgement
                </Button>
              )}
            </div>
          )}

          <div className="sitting__split" data-narrow={narrow ? 'true' : 'false'} ref={split}>
            {narrow ? (
              side === 'answer' ? (
                writing
              ) : (
                question
              )
            ) : questionHidden ? (
              <>
                <div className="sitting__collapsed">
                  <Button
                    icon="expand"
                    onClick={() => {
                      showQuestion(false)
                    }}
                  >
                    Show {leftInWords}
                  </Button>
                </div>
                {writing}
              </>
            ) : (
              <>
                <div className="sitting__pane" style={{ flexBasis: `${String(splitPct)}%` }}>
                  {question}
                </div>
                {/* A separator, not a gesture on its own: dragging moves it, but the arrow keys and
                    Home/End reach every position it can take just as well. */}
                <div
                  className="sitting__divider"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize the question and the answer"
                  aria-valuenow={Math.round(splitPct)}
                  aria-valuemin={SPLIT_MIN}
                  aria-valuemax={SPLIT_MAX}
                  tabIndex={0}
                  onPointerDown={onDividerPointerDown}
                  onPointerMove={onDividerPointerMove}
                  onKeyDown={onDividerKeyDown}
                />
                <div className="sitting__pane" style={{ flexBasis: `${String(100 - splitPct)}%` }}>
                  {writing}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
